'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetPositionsRow, useActiveWallet, useAuth, useGetMarket } from '@morfi/backend-sdk-v2/client';
import type { JSX } from 'react';
import { type OutcomeMarketData, PositionCard } from '@/components/market/position-card';
import { mergePositionCardRows } from '@/components/market/position-panel-rows';
import type { PositionCardRow } from '@/hooks/positions/user-portfolio/display-types';
import { useUserMarketPortfolioPositions } from '@/hooks/positions/user-portfolio/selectors';
import { useConfirmedPositionRows } from '@/hooks/positions/user-portfolio/use-confirmed-position-rows';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
import { useChartSettingsStore } from '@/stores/chart-settings';
interface MarketPositionPanelProps {
  /** The market pubkey whose held outcomes this panel rolls up. */
  marketPubkey: string;
  /** Canonical market query rows used to retain fully exited history. */
  positionRows: readonly GetPositionsRow[];
  /** Pool snapshot already loaded by the market-detail trade surface. */
  tradeData?: OnchainTradeData;
  className?: string;
}

/**
 * Market-detail "Your position" panel — the canonical `<PositionCard>` scoped
 * to every held or fully exited outcome in this market. Mounted in the right
 * sidebar directly under the trade form (and a full-width mobile slot),
 * mirroring the winner page's `WinnerPositionPanel`.
 *
 * Renders nothing until a wallet is connected; `<PositionCard>` itself returns
 * null when the viewer holds no position on the market.
 */
export function MarketPositionPanel(t0) {
  const $ = _c(36);
  const {
    marketPubkey,
    positionRows,
    tradeData,
    className
  } = t0;
  const {
    activeWallet
  } = useActiveWallet();
  const {
    user
  } = useAuth();
  const marketCapMode = useChartSettingsStore(_temp);
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const marketQuery = useGetMarket(t1);
  const wallet = activeWallet?.address ?? "";
  const t2 = user?.id ?? "";
  let t3;
  if ($[2] !== marketPubkey || $[3] !== t2 || $[4] !== wallet) {
    t3 = {
      userId: t2,
      wallet,
      market: marketPubkey
    };
    $[2] = marketPubkey;
    $[3] = t2;
    $[4] = wallet;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    rows: snapshotRows
  } = useUserMarketPortfolioPositions(t3);
  let t4;
  if ($[6] !== marketPubkey || $[7] !== positionRows || $[8] !== snapshotRows || $[9] !== wallet) {
    t4 = mergePositionCardRows({
      snapshotRows,
      indexedRows: positionRows,
      wallet,
      market: marketPubkey
    });
    $[6] = marketPubkey;
    $[7] = positionRows;
    $[8] = snapshotRows;
    $[9] = wallet;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const indexedRows = t4;
  const t5 = user?.id ?? "";
  let t6;
  if ($[11] !== tradeData) {
    t6 = tradeData === undefined ? {} : {
      tradeData
    };
    $[11] = tradeData;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== indexedRows || $[14] !== marketPubkey || $[15] !== t5 || $[16] !== t6 || $[17] !== wallet) {
    t7 = {
      userId: t5,
      wallet,
      rows: indexedRows,
      market: marketPubkey,
      ...t6
    };
    $[13] = indexedRows;
    $[14] = marketPubkey;
    $[15] = t5;
    $[16] = t6;
    $[17] = wallet;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const rows = useConfirmedPositionRows(t7);
  let marketOutcomes;
  let outcomeMarket;
  if ($[19] !== marketQuery.data?.outcomes) {
    marketOutcomes = marketQuery.data?.outcomes ?? [];
    outcomeMarket = {};
    for (const o of marketOutcomes) {
      outcomeMarket[Number(o.outcomeIndex)] = {
        effectiveMarketCapD6: o.effectiveMarketCapD6,
        indexedOutcomeMintSupplyD6: o.totalSupply,
        imageUrl: o.imageUrl,
        imageNsfw: o.imageNsfw
      };
    }
    $[19] = marketQuery.data?.outcomes;
    $[20] = marketOutcomes;
    $[21] = outcomeMarket;
  } else {
    marketOutcomes = $[20];
    outcomeMarket = $[21];
  }
  if (wallet === "") {
    return null;
  }
  let t8;
  if ($[22] !== tradeData) {
    t8 = tradeData !== undefined ? {
      tradeData
    } : {};
    $[22] = tradeData;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== marketOutcomes.length) {
    t9 = marketOutcomes.length > 0 ? {
      outcomeCount: marketOutcomes.length
    } : {};
    $[24] = marketOutcomes.length;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== className) {
    t10 = className !== undefined ? {
      className
    } : {};
    $[26] = className;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== marketCapMode || $[29] !== marketPubkey || $[30] !== outcomeMarket || $[31] !== rows || $[32] !== t10 || $[33] !== t8 || $[34] !== t9) {
    t11 = <PositionCard rows={rows} market={marketPubkey} marketCapMode={marketCapMode} outcomeMarket={outcomeMarket} {...t8} winnerHref={_temp2} {...t9} {...t10} />;
    $[28] = marketCapMode;
    $[29] = marketPubkey;
    $[30] = outcomeMarket;
    $[31] = rows;
    $[32] = t10;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp2(row) {
  return `/winners/${row.outcomeMint}`;
}
function _temp(s) {
  return s.chartMode === "live" && s.yAxisProperty === "marketCap";
}